import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { TABLES, getSchoolYears, resolveSchoolYear } from '../lib/tables';
import SchoolYearSelect from '../components/SchoolYearSelect';
import SchoolYearBadge from '../components/SchoolYearBadge';
import { CATEGORY_LABELS, downloadCsv } from '../lib/statistics';

const statusLabel = { validat: 'Validat', in_asteptare: 'În așteptare', respins: 'Respins' };
const statusColor = { validat: 'success', in_asteptare: 'warning', respins: 'error' };

export default function CoordinatorRequests() {
  const [yearOptions, setYearOptions] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [filters, setFilters] = useState({ status: 'all', modul: 'all', categorie: 'all', sursa: 'all', clasa: 'all' });
  const [rows, setRows] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ field: 'data', dir: 'desc' });
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let mounted = true;
    getSchoolYears(TABLES.credit_requests).then((years) => {
      if (!mounted) return;
      setYearOptions(years);
      setSelectedYear((prev) => prev ?? resolveSchoolYear(years));
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!selectedYear) return undefined;
    let mounted = true;
    setLoading(true);

    const load = async () => {
      let requestsQuery = supabase
        .from(TABLES.credit_requests)
        .select('id, id_elev, id_tip_credit, id_module, nume_activitate, ore_realizate, credite_solicitate, credite_acordate, status, data_cerere, data_validare, an_scolar, source_system, external_user_id, source_id, idempotency_key')
        .order('data_cerere', { ascending: false });
      if (selectedYear !== 'all') requestsQuery = requestsQuery.eq('an_scolar', selectedYear);

      const [requestsRes, profilesRes, modulesRes, typesRes] = await Promise.all([
        requestsQuery,
        supabase.from(TABLES.profiles).select('id, nume, prenume, clasa, specializare').eq('rol', 'elev'),
        supabase.from(TABLES.modules).select('id, cod_module, nume_module').order('ordine'),
        supabase.from(TABLES.credit_types).select('id, nume_tip, categorie'),
      ]);

      if (!mounted) return;
      const profiles = new Map((profilesRes.data || []).map((p) => [p.id, p]));
      const moduleList = modulesRes.data || [];
      const moduleMap = new Map(moduleList.map((m) => [m.id, m]));
      const typeMap = new Map((typesRes.data || []).map((t) => [t.id, t]));

      setModules(moduleList);
      setRows(
        (requestsRes.data || []).map((request) => {
          const profile = profiles.get(request.id_elev);
          const module = moduleMap.get(request.id_module);
          const type = typeMap.get(request.id_tip_credit);
          return {
            ...request,
            elev: profile ? `${profile.nume} ${profile.prenume}` : '-',
            clasa: profile?.clasa || '-',
            modul: module?.cod_module || '-',
            modulNume: module?.nume_module || '',
            categorie: type?.categorie || 'curricular',
            credite: request.credite_acordate || request.credite_solicitate || 0,
          };
        }),
      );
      setLoading(false);
    };

    load();
    return () => { mounted = false; };
  }, [selectedYear]);

  const classOptions = useMemo(() => [...new Set(rows.map((r) => r.clasa).filter((c) => c && c !== '-'))].sort(), [rows]);

  const filtered = useMemo(() => {
    let list = [...rows];
    if (filters.status !== 'all') list = list.filter((r) => r.status === filters.status);
    if (filters.modul !== 'all') list = list.filter((r) => r.modul === filters.modul);
    if (filters.categorie !== 'all') list = list.filter((r) => r.categorie === filters.categorie);
    if (filters.sursa !== 'all') list = list.filter((r) => (filters.sursa === 'srbhub' ? r.source_system === 'srbhub' : r.source_system !== 'srbhub'));
    if (filters.clasa !== 'all') list = list.filter((r) => r.clasa === filters.clasa);

    const { field, dir } = sort;
    list.sort((a, b) => {
      const values = {
        elev: a.elev,
        clasa: a.clasa,
        activitate: a.nume_activitate,
        modul: a.modul,
        ore: a.ore_realizate || 0,
        credite: a.credite,
        status: a.status,
        sursa: a.source_system === 'srbhub' ? 'srbhub' : 'manual',
        data: new Date(a.data_cerere).getTime(),
      };
      const other = {
        elev: b.elev,
        clasa: b.clasa,
        activitate: b.nume_activitate,
        modul: b.modul,
        ore: b.ore_realizate || 0,
        credite: b.credite,
        status: b.status,
        sursa: b.source_system === 'srbhub' ? 'srbhub' : 'manual',
        data: new Date(b.data_cerere).getTime(),
      };
      const aValue = values[field];
      const bValue = other[field];
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return dir === 'asc' ? aValue - bValue : bValue - aValue;
      }
      return dir === 'asc'
        ? String(aValue || '').localeCompare(String(bValue || ''))
        : String(bValue || '').localeCompare(String(aValue || ''));
    });
    return list;
  }, [rows, filters, sort]);

  const handleSort = (field) => {
    setSort((prev) => ({ field, dir: prev.field === field && prev.dir === 'desc' ? 'asc' : 'desc' }));
  };

  const handleExport = () => {
    downloadCsv(
      `cereri_${selectedYear}.csv`,
      ['Elev', 'Clasa', 'Activitate', 'Modul', 'Ore', 'Credite', 'Status', 'Sursa', 'Data'],
      filtered.map((r) => [r.elev, r.clasa, r.nume_activitate, r.modul, r.ore_realizate, r.credite, statusLabel[r.status] || r.status, r.source_system === 'srbhub' ? 'srbhub' : 'manual', r.data_cerere]),
    );
  };

  if (!selectedYear || loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Toate cererile</Typography>
          <Typography variant="body2" color="text.secondary">
            Cereri din tot sistemul, inclusiv importurile automate SRBHub
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <SchoolYearSelect value={selectedYear} options={yearOptions} onChange={setSelectedYear} />
          <SchoolYearBadge year={selectedYear} />
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport}>Export CSV</Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <MenuItem value="all">Toate</MenuItem>
            <MenuItem value="in_asteptare">În așteptare</MenuItem>
            <MenuItem value="validat">Validate</MenuItem>
            <MenuItem value="respins">Respinse</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Modul</InputLabel>
          <Select label="Modul" value={filters.modul} onChange={(e) => setFilters({ ...filters, modul: e.target.value })}>
            <MenuItem value="all">Toate</MenuItem>
            {modules.map((m) => <MenuItem key={m.id} value={m.cod_module}>{m.cod_module}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Categorie</InputLabel>
          <Select label="Categorie" value={filters.categorie} onChange={(e) => setFilters({ ...filters, categorie: e.target.value })}>
            <MenuItem value="all">Toate</MenuItem>
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Sursă</InputLabel>
          <Select label="Sursă" value={filters.sursa} onChange={(e) => setFilters({ ...filters, sursa: e.target.value })}>
            <MenuItem value="all">Toate</MenuItem>
            <MenuItem value="manual">Manuale</MenuItem>
            <MenuItem value="srbhub">SRBHub</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Clasă</InputLabel>
          <Select label="Clasă" value={filters.clasa} onChange={(e) => setFilters({ ...filters, clasa: e.target.value })}>
            <MenuItem value="all">Toate</MenuItem>
            {classOptions.map((clasa) => <MenuItem key={clasa} value={clasa}>{clasa}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {filtered.length} din {rows.length} cereri
      </Typography>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {[
                { id: 'elev', label: 'Elev' },
                { id: 'clasa', label: 'Clasă' },
                { id: 'activitate', label: 'Activitate' },
                { id: 'modul', label: 'Modul' },
                { id: 'ore', label: 'Ore' },
                { id: 'credite', label: 'Credite' },
                { id: 'status', label: 'Status' },
                { id: 'sursa', label: 'Sursă' },
                { id: 'data', label: 'Data' },
              ].map((column) => (
                <TableCell key={column.id}>
                  <TableSortLabel
                    active={sort.field === column.id}
                    direction={sort.field === column.id ? sort.dir : 'asc'}
                    onClick={() => handleSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.slice(0, 300).map((row) => (
              <TableRow key={row.id} hover onClick={() => setDetail(row)} sx={{ cursor: 'pointer' }}>
                <TableCell>{row.elev}</TableCell>
                <TableCell>{row.clasa}</TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>{row.nume_activitate}</Typography>
                </TableCell>
                <TableCell>{row.modul}</TableCell>
                <TableCell>{row.ore_realizate || 0}</TableCell>
                <TableCell><strong>{row.credite}</strong></TableCell>
                <TableCell>
                  <Chip size="small" label={statusLabel[row.status] || row.status} color={statusColor[row.status] || 'default'} />
                </TableCell>
                <TableCell>
                  {row.source_system === 'srbhub' ? (
                    <Chip size="small" label="importat automat" sx={{ bgcolor: '#000000', color: '#ffffff' }} />
                  ) : (
                    <Chip size="small" variant="outlined" label="manual" />
                  )}
                </TableCell>
                <TableCell>
                  {row.data_cerere ? new Date(row.data_cerere).toLocaleDateString('ro-RO') : '-'}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Nu există cereri pentru filtrele selectate.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Detalii cerere #{detail?.id}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}><strong>Elev:</strong> {detail?.elev} ({detail?.clasa})</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}><strong>Activitate:</strong> {detail?.nume_activitate}</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}><strong>Modul:</strong> {detail?.modul} {detail?.modulNume}</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}><strong>Ore:</strong> {detail?.ore_realizate || 0}</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}><strong>Credite:</strong> {detail?.credite}</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}><strong>Status:</strong> {statusLabel[detail?.status] || detail?.status}</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}><strong>An școlar:</strong> {detail?.an_scolar || '-'}</Typography>
          {detail?.source_system === 'srbhub' && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Sursă:</strong> SRBHub (import automat)</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><strong>ID extern:</strong> {detail?.external_user_id || '-'}</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Club (source_id):</strong> {detail?.source_id || '-'}</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                <strong>Idempotency key:</strong> {detail?.idempotency_key || '-'}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Payload-ul original se păstrează în integration_inbox_v2 (acces intern).
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Închide</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
